<template>
  <RCard class="todo-list">
    <TodoModal
      v-model="createModal"
      :states-with-tasks="statesWithTasks"
      :from-page="fromPage"
      @new-todo="newTodoCreated"
    />

    <TodoModal
      v-model="editModal"
      :states-with-tasks="statesWithTasks"
      :edit-item="itemToEdit"
      :from-page="fromPage"
      @updated-todo="todoUpdated"
      @delete="deleteTodo(itemToEdit.id)"
    />

    <Grid gaf="column" justify-content="space-between" align-items="center">
      <div class="text-subtitle-1 text-secondary-2">Tasks</div>

      <Grid gaf="column" align-items="center" gap="var(--r-space-md)">
        <PlusIcon @click.native="createModal = true" />

        <RBtn type="link" to="/todo-templates">manage templates</RBtn>

        <RDropdown
          :value="users ? selectedUser : null"
          :options="users || []"
          :loading="!users"
          placeholder="Select user"
          option-label="name"
          option-value="id"
          no-label
          no-hint
          style="width: 200px"
          @input="selectedUser = $event"
        />
      </Grid>
    </Grid>

    <Grid gaf="column" justify-content="start" gap="var(--r-space-sp)" class="q-mt-md text-secondary-5">
      <div
        :class="todoFilter === 'Open' ? 'text-overline-1' : 'text-caption-1'"
        class="cursor-pointer"
        @click="todoFilter = 'Open'"
      >
        <Grid gaf="column" justify-content="start" gap="var(--r-space-xs)">
          Open
          <Grid
            v-if="unCompleted.length"
            justify-content="center"
            align-items="center"
            align-content="center"
            class="uncompleted-badge text-caption-2 bg-primary-1 text-white-1"
          >
            {{ unCompleted.length }}
          </Grid>
        </Grid>

        <div v-if="todoFilter === 'Open'" class="line"></div>
      </div>

      <div
        :class="todoFilter === 'Completed' ? 'text-overline-1' : 'text-caption-1'"
        class="cursor-pointer"
        @click="todoFilter = 'Completed'"
      >
        <div>Completed</div>
        <div v-if="todoFilter === 'Completed'" class="line"></div>
      </div>
    </Grid>

    <ToDoTable
      :todos="filteredTodos"
      :filter="todoFilter"
      @done-changed="doneChanged"
      @edit="editClicked"
      @delete="deleteTodo($event.id)"
    />
  </RCard>
</template>

<script>
import * as apiTo from './api'
// Libraries
import axios from 'axios'
import { mapState } from 'vuex'
import { cloneDeep, orderBy } from 'lodash'
// Components
import ToDoTable from './components/ToDoTable'
import TodoModal from '@/reusable/TodoModal/TodoModal'
import PlusIcon from '@/ui/icons/PlusIcon'

export default {
  name: 'ToDoList',

  components: {
    ToDoTable,
    TodoModal,
    PlusIcon
  },

  props: {
    component: Object,
    title: String,
    contextUrl: String,
    fromPage: String
  },

  data() {
    return {
      createModal: false,
      showCompleted: false,
      todos: [],
      loading: false,
      todoFilter: 'Open',
      itemToEdit: null,
      editModal: false,
      states: null,
      users: null,
      selectedUser: { id: this.$store.state.user.id, name: this.$store.state.user.name }
    }
  },

  computed: {
    ...mapState('todo-modal', ['createData']),

    statesWithTasks() {
      if (!this.createData) return
      const states = cloneDeep(this.createData.states)
      const todos = cloneDeep(this.todos)
      return states.map(s => {
        s.tasks = todos
          .filter(t => t.state && t.state.id === s.id)
          .map(t => ({ ...t, kanban_position: Number(t.kanban_position) }))
        if (s.tasks.length) s.tasks = orderBy(s.tasks, 'kanban_position')
        return s
      })
    },

    filteredTodos() {
      let todos = cloneDeep(this.todos)
      if (this.fromPage === 'dashboard') todos = todos.filter(t => t.in_dashboard)
      if (this.todoFilter === 'Completed') return todos.filter(t => t.state && t.state.state_type.name === 'completed')
      else return todos.filter(t => !t.state || t.state.state_type.name !== 'completed')
    },

    unCompleted() {
      return cloneDeep(this.todos).filter(t => !t.state || t.state.state_type.name !== 'completed')
    },

    url() {
      let url = '/to-dos'
      if (this.contextUrl) url += this.contextUrl
      return url
    }
  },

  watch: {
    unCompleted(val) {
      this.$emit('uncompleted-count', val.length)
    },

    selectedUser(user) {
      this.loading = true
      this.getTodos(user.id)
        .then(({ data }) => (this.todos = data.result))
        .finally(() => (this.loading = false))
    }
  },

  created() {
    this.getTodosStatesAndOwners()
  },

  methods: {
    getTodosStatesAndOwners() {
      this.loading = true
      axios
        .all([this.getTodos(this.selectedUser.id), apiTo.getStates(), apiTo.getUsers()])
        .then(
          axios.spread(({ data: todosApi }, { data: statesApi }, { data: usersApi }) => {
            this.todos = todosApi.result
            this.states = statesApi.result
            this.users = [{ id: null, name: 'All users' }, ...usersApi.result]
            const query = this.$route.query
            if (!!query['new-todo']) this.createModal = true
          })
        )
        .finally(() => (this.loading = false))
    },

    getTodos(ownerID) {
      return apiTo.getTodos(this.url, { ...(ownerID && { 'filter[owners.id]': ownerID }) })
    },

    mapStatesByBoard() {
      // NOTE this code is for memoization in order to loop through the remaining states
      let memoizeStates = [...this.states]
      const getStatesForBoard = boardId => {
        const filteredStates = []
        const unfilteredStates = []
        memoizeStates.forEach(state => {
          if (state.board.id === boardId) filteredStates.push(state)
          else unfilteredStates.push(state)
        })
        memoizeStates = unfilteredStates
        return filteredStates
      }

      return this.states.reduce((acc, curr) => {
        if (!acc.some(b => b.id === curr.board.id)) {
          const states = getStatesForBoard(curr.board.id).map(s => ({
            id: s.id,
            type: s.state_type
          }))
          acc.push({ ...curr.board, states })
        }
        return acc
      }, [])
    },

    doneChanged(item) {
      const boards = this.mapStatesByBoard()
      const getBoard = inKanban => {
        if (inKanban) return boards.find(b => b.states.some(s => s.id === item.state.id))
        return boards.find(b => b.system)
      }
      const board = getBoard(item.in_kanban)
      const completedState = board.states.find(s => s.type.name === 'completed')
      const unscheduledState = board.states.find(s => s.type.name === 'unscheduled')
      const stateId = item.state && item.state.id === completedState.id ? unscheduledState.id : completedState.id

      const payload = {
        title: item.title,
        body: item.body,
        kanban_position: item.kanban_position,
        deadline: item.deadline,
        entity: item.entity ? item.entity.type : null,
        entity_id: item.entity ? item.entity.id : null,
        priority_id: item.priority ? item.priority.id : null,
        owner_ids: item.owners.map(o => o.id),
        state_id: stateId,
        in_kanban: item.in_kanban,
        in_dashboard: item.in_dashboard
      }

      this.updateTodo(payload, item.id)
    },

    updateTodo(payload, id) {
      this.loading = true
      apiTo
        .updateTodo(id, payload)
        .then(({ data }) => this.todoUpdated(data.result))
        .finally(() => (this.loading = false))
    },

    deleteTodo(id) {
      this.loading = true
      apiTo
        .deleteTodo(id)
        .then(() => {
          const index = this.todos.findIndex(todo => todo.id === id)
          if (~index) this.todos.splice(index, 1)
        })
        .finally(() => (this.loading = false))
    },

    editClicked(item) {
      this.itemToEdit = item
      this.editModal = true
    },

    todoOwnersMatchSelectedOwner(todo) {
      const todoHasOwner = !!todo.owners.length
      const anOwnerIsSelected = !!this.selectedUser.id
      const selectedUserIsIncludedInTodosOwners = !!todo.owners.find(o => o.id === this.selectedUser.id)

      if (!todoHasOwner && anOwnerIsSelected) return false
      return !(todoHasOwner && anOwnerIsSelected && !selectedUserIsIncludedInTodosOwners)
    },

    todoUpdated(newTodo) {
      const index = this.todos.findIndex(todo => todo.id === newTodo.id)
      if (this.todoOwnersMatchSelectedOwner(newTodo)) this.todos.splice(index, 1, newTodo)
      else this.todos.splice(index, 1)
    },

    newTodoCreated(todo) {
      if (!this.todoOwnersMatchSelectedOwner(todo)) return
      this.todos.unshift(todo)
    }
  }
}
</script>

<style lang="scss" scoped>
.todo-list {
  .line {
    border-top: 2px solid var(--r-primary-1);
  }

  .uncompleted-badge {
    width: 12px;
    height: 12px;
    border-radius: 50%;
  }
}
</style>
